import { Visibility, VisibilityOff } from "@mui/icons-material";
import type React from "react";
import { useState } from "react";
import InfoTooltip, { type TooltipPosition } from "~/components/ui/infoTooltip";
import styles from "./index.module.scss";

interface FormFieldProps {
  label: string;
  name: string;
  type?: "text" | "textarea" | "date" | "password"; // Support text, textarea, date, and password inputs
  placeholder?: string;
  required?: boolean;
  value: string;
  // biome-ignore lint/suspicious/noExplicitAny: FormField accepts both input and textarea change events from various callers
  onChange: (e: any) => void;
  maxLength?: number;
  infoMessage?: string;
  infoPosition?: TooltipPosition;
  disabled?: boolean;
  readOnly?: boolean;
  suffix?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = "text",
  placeholder = "",
  required = false,
  value,
  onChange,
  maxLength,
  infoMessage,
  infoPosition = "top",
  disabled = false,
  readOnly = false,
  suffix,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  // 残り文字数を計算
  const remainingChars = maxLength ? maxLength - (value?.length || 0) : null;

  // パスワードフィールドの実際のtype
  const inputType = type === "password" ? (showPassword ? "text" : "password") : type;

  // 文字数超過フラグ
  const isExceeded = maxLength && remainingChars !== null && remainingChars < 0;

  return (
    <div className={styles["formField"]}>
      <label htmlFor={name}>
        <span className={styles["labelText"]}>
          {label}
          {required && <span className={styles["required"]}>*</span>}
        </span>
        {infoMessage && (
          <InfoTooltip
            message={infoMessage}
            className={styles["infoTooltip"] ?? ""}
            position={infoPosition}
          />
        )}
      </label>
      {type === "textarea" ? (
        <>
          <textarea
            id={name}
            name={name}
            placeholder={placeholder}
            className={`${styles["inputField"]} ${isExceeded ? styles["exceeded"] : ""} ${readOnly ? styles["readOnly"] : ""}`}
            onChange={onChange}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            aria-readonly={readOnly || undefined}
          />
          {maxLength && (
            <div className={`${styles["charCount"]} ${isExceeded ? styles["exceeded"] : ""}`}>
              残り {remainingChars} 文字
            </div>
          )}
        </>
      ) : (
        <>
          <div
            className={
              type === "password"
                ? styles["passwordWrapper"]
                : suffix
                  ? styles["suffixWrapper"]
                  : undefined
            }
          >
            <input
              id={name}
              type={inputType}
              name={name}
              placeholder={placeholder}
              className={`${styles["inputField"]} ${isExceeded ? styles["exceeded"] : ""} ${type === "password" ? styles["passwordInput"] : ""} ${readOnly ? styles["readOnly"] : ""} ${suffix ? styles["suffixInput"] : ""}`}
              onChange={onChange}
              value={value}
              disabled={disabled}
              readOnly={readOnly}
              aria-readonly={readOnly || undefined}
              data-testid={name === "name" ? "project-name" : undefined}
            />
            {suffix && (
              <span className={styles["suffixText"]} aria-hidden="true">
                {suffix}
              </span>
            )}
            {type === "password" && (
              <button
                type="button"
                className={styles["passwordToggle"]}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "パスワードを非表示" : "パスワードを表示"}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </button>
            )}
          </div>
          {maxLength && type !== "date" && (
            <div className={`${styles["charCount"]} ${isExceeded ? styles["exceeded"] : ""}`}>
              残り {remainingChars} 文字
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FormField;
